/**
 * Hook personalizado para cargar datos de soporte
 * 
 * Referencia: sistema-tickets-frontend-COMPLETO.md Sección 6.2
 */

import { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { ErpContext } from '../../context/ErpContext';

/**
 * Hook useSoporteData
 * 
 * Hook personalizado para cargar datos de la API de soporte
 * 
 * @param {string} endpoint - Endpoint de la API (ej: '/tickets', '/categorias')
 * @param {Object} params - Parámetros de query (opcional)
 * @returns {Object} - { data, loading, error, reload }
 * 
 * @example
 * const { data, loading, error, reload } = useSoporteData('/tickets', { status: 'nuevo' });
 */
export const useSoporteData = (endpoint, params = {}) => {
  const { serverURL } = useContext(ErpContext);
  
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  /**
   * Función para cargar datos
   * IMPORTANTE: Recibir params como argumento para evitar problemas de closure
   */
  const cargar = useCallback(async (currentParams) => {
    if (!endpoint || !serverURL) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      console.log(`🔍 [useSoporteData] Llamando a: ${serverURL}/soporteAPI${endpoint}`);
      console.log(`🔍 [useSoporteData] Parámetros recibidos:`, currentParams);
      
      const response = await axios.get(`${serverURL}/soporteAPI${endpoint}`, {
        withCredentials: true,
        params: currentParams || {}
      });
      
      // Manejar diferentes estructuras de respuesta
      // Puede venir como { success: true, data: [...] } o directamente como array/objeto
      if (response.data.success !== undefined) {
        // Si tiene estructura { success: true, ... }
        setData(response.data);
      } else {
        // Si viene directamente el dato
        setData(response.data);
      }
    } catch (err) {
      console.error(`Error cargando ${endpoint}:`, err);
      setError(err.response?.data?.error || err.message || 'Error al cargar datos');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [serverURL, endpoint]); // No incluir params aquí para evitar bucles
  
  /**
   * Cargar datos al montar o cuando cambian endpoint/params
   */
  useEffect(() => {
    if (endpoint && serverURL) {
      // Pasar params explícitamente para usar el valor actual
      cargar(params);
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, serverURL, JSON.stringify(params)]); // Usar JSON.stringify para comparar params
  
  return { 
    data, 
    loading, 
    error, 
    reload: () => cargar(params) // Pasar params actuales al recargar
  };
};

